import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

// out of the box dayjs only supports the ISO format YYYY-MM-DD
// it needs to use the custom parser for other formats
dayjs.extend(customParseFormat)

const ISO_DATE_FORMAT = 'YYYY-MM-DD' // dayjs format

interface DateUtilResult {
  normalizePattern: (value: string) => string
  fromDate: (date: Date) => dayjs.Dayjs
  dayjsValue: (value: string | Date | dayjs.Dayjs | null, dateFormat?: string) => dayjs.Dayjs | null
  format: (date: Date, pattern: string) => string
  isValid: (date: Date) => boolean
  parse: (dateString: string, pattern?: string) => Date
  isISODate: (dateString: string | undefined) => boolean
  toISODate: (date: Date | undefined) => string
  toISODateFromParts: (year: number, month: number, day: number) => string
  fromISODate: (dateString: string, pattern: string) => string
  formatDateTime: (date: Date | string, locale?: string) => string
  isMaxTimeExceeded: (args: { startTime: Date; maxTime: number }) => boolean
}

const dateUtil = (): DateUtilResult => {
  // dayjs uses uppercase YYYY, MM, DD
  // https://day.js.org/docs/en/parse/string-format
  const normalizePattern = (pattern: string) => {
    return pattern?.replaceAll('y', 'Y')?.replaceAll('d', 'D')?.replaceAll(' ', '')
  }

  const stripSpaces = (value: string): string => {
    return value?.replaceAll(' ', '')
  }

  const fromDate = (date: Date): dayjs.Dayjs => dayjs(date)

  const dayjsValue = (value: string | Date | dayjs.Dayjs | null, pattern = 'YYYY-MM-DD'): dayjs.Dayjs | null => {
    if (!value) {
      return null
    }
    if (typeof value === 'string') {
      return dayjs(stripSpaces(value), normalizePattern(pattern), true)
    }
    if (value instanceof Date) {
      return dayjs(value)
    }
    return value
  }

  const isValid = (date: Date | string | number | undefined): boolean => {
    return date instanceof Date && !isNaN(date.getTime())
  }

  const format = (date: Date, pattern = 'yyyy-MM-dd'): string => {
    if (!isValid(date)) {
      return ''
    }

    const normalizedPattern = normalizePattern(pattern)
    return dayjs(date).format(normalizedPattern)
  }

  const parse = (dateString: string, pattern = 'yyyy-MM-dd'): Date => {
    return dayjs(stripSpaces(dateString), normalizePattern(pattern), true).toDate()
  }

  const toISODate = (date: Date | undefined): string => {
    return isValid(date) ? dayjs(date).format(ISO_DATE_FORMAT) : ''
  }

  const toISODateFromParts = (year: number, month: number, day: number): string => {
    return dayjs.utc().year(year).month(month).date(day).format(ISO_DATE_FORMAT)
  }

  const isISODate = (date: string | undefined): boolean => {
    if (!date) return false

    return /^(19|20)\d{2}\-(0[1-9]|1[0-2])\-(0[1-9]|1\d|2\d|3[01])$/.test(date.substring(0, ISO_DATE_FORMAT.length))
  }

  const fromISODate = (dateString: string, pattern = 'MM/dd/yyyy'): string => {
    const dateToParse = dateString?.substring(0, ISO_DATE_FORMAT.length)
    if (!isISODate(dateToParse)) {
      return dateString
    }

    const dayjsValue = dayjs.utc(stripSpaces(dateToParse), ISO_DATE_FORMAT, true)
    return dayjsValue.format(normalizePattern(pattern))
  }

  /**
   * Format the date as long format, e.g. August 10, 2023 3:00PM
   * @param date date value
   * @param locale locale, e.g. en-ca, fr-ca
   * @returns the formatted date value as string
   */
  const formatDateTime = (date: Date | string, locale?: string): string => {
    let dateValue: number | Date

    if (isValid(date)) {
      dateValue = date as Date
    } else {
      dateValue = new Date(date as string)
      if (!isValid(dateValue)) {
        return ''
      }
    }

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    }

    const dateTimeFormat = new Intl.DateTimeFormat(locale || 'en-ca', options)

    const parts = dateTimeFormat.formatToParts(dateValue)

    const amPm = `${parts
      .find(p => p.type === 'dayPeriod')
      ?.value?.replaceAll('.', '')
      ?.toUpperCase()}`

    const displayValue =
      `${parts.find(p => p.type === 'month')?.value} ` +
      `${parts.find(p => p.type === 'day')?.value}, ` +
      `${parts.find(p => p.type === 'year')?.value} ` +
      `${parts.find(p => p.type === 'hour')?.value}:${parts.find(p => p.type === 'minute')?.value}${amPm}`

    return displayValue
  }

  const isMaxTimeExceeded = (args: { startTime: Date; maxTime: number }): boolean => {
    const { startTime, maxTime } = args

    const endTime = new Date()
    const timeDiff = endTime.getTime() - startTime.getTime()
    return timeDiff >= maxTime
  }

  return {
    normalizePattern,
    fromDate,
    dayjsValue,
    format,
    isValid,
    parse,
    isISODate,
    toISODate,
    toISODateFromParts,
    fromISODate,
    formatDateTime,
    isMaxTimeExceeded,
  }
}

export const DateUtil = dateUtil()
